'use client'

import { useState } from 'react'

import {
  type ProductReviewServiceReducedReviewV1,
  type ProductReviewServiceSummaryResponseV1,
} from '@redteclab/api/clients/bully'

import { type ProductReviewsCustomerReview } from '../../../../product'
import { LANGUAGE } from '../../../../tenant'
import { productReviewAccessorGetAllSummary } from '../../../model/productReviewAccessor'
import { type ProductPageReviewData } from '../../../productPageDataAssembly/assembleReviews'

export const convertProductReviewToComponentReviewType = (
  review: ProductReviewServiceReducedReviewV1,
): ProductReviewsCustomerReview => {
  return {
    customerName: review.customer.author,
    id: review.reviewId,
    message: review.message,
    rating: review.rating,
    submissionDate: review.submissionDate,
    title: review.title,
  }
}

type SelectableLanguageData = {
  reviewsCount: number
  text: string
  value: string
}

/**
 * all lanugages that shall be shown in select dropdown
 */
const allReviewLanguages = [LANGUAGE.NL, LANGUAGE.DE, LANGUAGE.FR, LANGUAGE.IT]

/**
 * API response does not include languages with 0 reviews, but we still need
 * to render them in dropdown. This add's the lanugages that are not present in the response and sets 0 reviews on such.
 */
const addLanguagesWithoutReviews = (
  selectableLanguageData: SelectableLanguageData[],
): SelectableLanguageData[] => {
  for (const language of allReviewLanguages) {
    if (selectableLanguageData.every((it) => it.value !== language)) {
      selectableLanguageData.push({
        reviewsCount: 0,
        text: language,
        value: language,
      })
    }
  }

  return selectableLanguageData
}

/**
 * maps API review languages data to to what is expected by the dropdown
 * response includes "all" language, this will be filtered out.
 * as well we still have to show in dropdown languages that have no reviews, response does not include them,
 * so we add them here by default.
 */
const prepareSelectableLanguageData = (
  reviewData: ProductPageReviewData,
): SelectableLanguageData[] => {
  const withoutAllData =
    reviewData.allLanguagesSummaries
      ?.map((it) => {
        return {
          reviewsCount: it.ratingCount,
          text: it.language,
          value: it.language,
        }
      })
      // API response inlcludes 'all' to show summary for all languages, this should not be selectable
      .filter((it) => it.value !== 'all') ?? []

  return addLanguagesWithoutReviews(withoutAllData)
}

/**
 * this hook encapsulates the logic of presenting the product reviews:
 * - On some tenants, depending on feature toggle, reviews from multiple languages can be shown.
 * - multireview language is treated as active when {@link ProductPageReviewData} have `allLanguagesSummary`
 */
export const useProductReviewsControls = ({
  reviewData,
  reviewsPreloadedOnServer,
}: {
  reviewData: ProductPageReviewData
  reviewsPreloadedOnServer?: ProductReviewServiceReducedReviewV1[]
}): {
  currentReviewsLanguage: string
  prefetchedDataForLanguage?: ProductReviewsCustomerReview[]
  reviewsCount: number
  reviewsSummary: ProductReviewServiceSummaryResponseV1
  selectableLanguageData: {
    reviewsCount: number
    text: string
    value: string
  }[]
  setCurrentReviewsLanguage: (value: string) => void
  showLanguageSwitcher: boolean
} => {
  /**
   * if tenant has feature "reviews from multiple languages",
   * this will represent the language that user will select from the dropdown on reviews.
   */
  const [currentReviewsLanguage, setCurrentReviewsLanguage] = useState<string>(
    reviewData.languageUsedToLoadReviewsOnServer,
  )

  /**
   * on server reviews are fetched for the "global language", this needs to be shown on initial load,
   * for other languages that selected afterwards there's no data, and it will be loaded via call.
   */
  const prefetchedDataForLanguage =
    reviewData.languageUsedToLoadReviewsOnServer === currentReviewsLanguage
      ? reviewsPreloadedOnServer?.map((review) =>
          convertProductReviewToComponentReviewType(review),
        )
      : undefined

  /** language data that will be used in language selection dropdown */
  const selectableLanguageData = prepareSelectableLanguageData(reviewData)

  /** if summaries for multi languages present, summary taken from "all" language */
  const reviewsSummary = productReviewAccessorGetAllSummary(reviewData)

  const reviewsCount =
    reviewData.allLanguagesSummaries?.find(
      (it) => it.language === currentReviewsLanguage,
    )?.ratingCount ?? reviewData.productReviews.totalItems

  return {
    currentReviewsLanguage,
    prefetchedDataForLanguage,
    reviewsCount,
    reviewsSummary,
    selectableLanguageData,
    setCurrentReviewsLanguage,
    showLanguageSwitcher: reviewData.showReviewLanguageSelection,
  }
}
